import { isBuoyDevice } from 'features/Integrations/features/ConnectedDevices/features/Buoy/utils/buoyDevice.utils';
import { getIcon } from 'features/Integrations/features/ConnectedDevices/features/DeviceMap/utils/deviceMap.utils';
import {
  openDeviceSettings,
  setSelectedDeviceId,
} from 'features/Integrations/state/slices/deviceSlice';
import { setSelectedDevice } from 'features/Integrations/state/thunks';
import { DeviceIsDeviceType } from 'features/Integrations/utils/deviceComparison.utils';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Marker, Popup, useMap } from 'react-leaflet';
import { useDispatch } from 'react-redux';
import DeviceMapPopup from '../popup/DeviceMapPopup/DeviceMapPopup';

const DeviceMarker = ({
  device,
  coords,
  setMapCenter,
  setUseThisDevice,
  setShowCurrentDevice,
  setShowDeviceSettings,
  selectedDeviceId,
}) => {
  const dispatch = useDispatch();
  const map = useMap();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const markerIcon = useMemo(() => getIcon(device), [device]);
  const markerEventHandlers = useMemo(
    () => ({
      popupopen: () => setIsPopupOpen(true),
      popupclose: () => setIsPopupOpen(false),
    }),
    [],
  );

  const fillDataforSimpleUSGS = async (simplePoint) => {
    try {
    } catch (error) {
      console.error('Error fetching detailed data for USGS device:', error);
      return simplePoint; // Return original point on error
    }
  };

  const markerKey = `${device?.system_type ?? 'unknown'}-${device?.node_id}`;

  // Ref for the popup
  const popupRef = useRef();

  // Open/close popup based on selectedDeviceId
  useEffect(() => {
    if (!popupRef.current) return;
    if (selectedDeviceId === device.id) {
      popupRef.current._source.openPopup();
    } else {
      popupRef.current._source.closePopup();
    }
  }, [selectedDeviceId, device.id]);

  return (
    <Marker key={markerKey} position={coords} icon={markerIcon} eventHandlers={markerEventHandlers}>
      <Popup ref={popupRef} className="base-map-popup-leaflet" closeButton={false}>
        {isPopupOpen && (
          <DeviceMapPopup
            device={device}
            onClose={() => map.closePopup()}
            onViewDevice={() => {
              map.closePopup();
              setMapCenter(coords);
              if (device?.tempPoint) {
                // This branch is for temporary USGS points that are not full devices.
                fillDataforSimpleUSGS(device.id); // TODO: Evaluate if this is still needed.
              }
              if (!device) return;

              // Select by device id so map, graph, and list stay in sync.
              dispatch(setSelectedDevice({ index: -1, id: device.id }));
              dispatch(setSelectedDeviceId(device.id));
              setUseThisDevice(device);

              // Flood and USGS monitoring stations both open their datasheets.
              const isDatasheetDevice =
                DeviceIsDeviceType(device, 'Stage') ||
                DeviceIsDeviceType(device, 'Monitoring Station') ||
                isBuoyDevice(device);
              if (isDatasheetDevice) {
                setShowCurrentDevice(true);
              } else {
                setShowDeviceSettings(true);
                dispatch(openDeviceSettings(device.id));
              }
            }}
          />
        )}
      </Popup>
    </Marker>
  );
};

const haveSameCoordinates = (previousCoords, nextCoords) =>
  previousCoords?.[0] === nextCoords?.[0] && previousCoords?.[1] === nextCoords?.[1];

const deviceMarkerPropsAreEqual = (previousProps, nextProps) => {
  const deviceId = nextProps.device?.id;
  const selectionAffectsMarker =
    previousProps.selectedDeviceId === deviceId || nextProps.selectedDeviceId === deviceId;

  return (
    previousProps.device === nextProps.device &&
    haveSameCoordinates(previousProps.coords, nextProps.coords) &&
    previousProps.setMapCenter === nextProps.setMapCenter &&
    previousProps.setUseThisDevice === nextProps.setUseThisDevice &&
    previousProps.setShowCurrentDevice === nextProps.setShowCurrentDevice &&
    previousProps.setShowDeviceSettings === nextProps.setShowDeviceSettings &&
    (!selectionAffectsMarker || previousProps.selectedDeviceId === nextProps.selectedDeviceId)
  );
};

export default memo(DeviceMarker, deviceMarkerPropsAreEqual);
